import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import replaceText from '../utils/trip-sheppered-text';
import Head from 'next/head';
import Image from 'next/image';

const PrivacyPolicy = () => {
  const router = useRouter();
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [isLargeScreen, setIsLargeScreen] = useState(false);
  const [showMobileNav, setShowMobileNav] = useState(false);
  const { ref } = router.query;
  const tableOfContents = [
    { id: 'scope', title: 'The Scope of This Policy' },
    { id: 'info-collect', title: 'The Information We Collect' },
    { id: 'info-provide', title: 'A. Information You Provide to Us' },
    {
      id: 'info-platform',
      title: 'B. Information We Collect When You Use the Platform'
    },
    {
      id: 'info-sources',
      title: 'C. Information We Collect From Other Sources'
    },
    { id: 'info-use', title: 'How We Use Your Information' },
    { id: 'info-disclose', title: 'How We Disclose Your Information' },
    {
      id: 'see-sight-tours-users',
      title: 'A. Sharing Between See Sight Tours Users'
    },
    {
      id: 'service-providers',
      title:
        'B. Disclosures to Service Providers to Provide See Sight Tours Mobile Application, and Other Parties'
    },
    {
      id: 'legal-reasons',
      title: 'C. For Legal Reasons and to Protect the See Sight Tours Platform'
    },
    { id: 'sale-or-merger', title: 'D. In Connection with the Sale or Merger' },
    { id: 'further-direction', title: 'E. Upon Your Further Direction' },
    { id: 'info-protect', title: 'How We Store and Protect Your Information' },
    { id: 'data-retention', title: 'Data Retention' },
    {
      id: 'rights-and-choices',
      title: 'Your Rights And Choices Regarding Your Data'
    },
    { id: 'users', title: 'A. All Users' },
    { id: 'location-specific', title: 'B. Location-Specific Rights' },
    { id: 'terms-of-service', title: 'SMS Terms of Service' }
  ];

  useEffect(() => {
    const checkScreenSize = () => {
      setIsLargeScreen(window.innerWidth >= 1024);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (!isLargeScreen) return;

      const sectionElements = tableOfContents
        .map(item => document.getElementById(item.id))
        .filter(Boolean) as HTMLElement[];

      let mostVisibleSection: HTMLElement | null = null;
      let maxVisibility = 0;

      sectionElements.forEach(section => {
        if (!section) return;

        const rect = section.getBoundingClientRect();
        const contentElement = document.querySelector(
          '.lg\\:overflow-y-auto.lg\\:max-h-screen'
        );
        const containerHeight =
          contentElement?.clientHeight || window.innerHeight;

        const visibleTop = Math.max(0, rect.top);
        const visibleBottom = Math.min(containerHeight, rect.bottom);
        const visibleHeight = Math.max(0, visibleBottom - visibleTop);

        const visibility =
          visibleHeight > 0
            ? 1 + (containerHeight - visibleTop) / containerHeight
            : 0;

        if (visibility > maxVisibility) {
          maxVisibility = visibility;
          mostVisibleSection = section;
        }
      });

      const contentElement = document.querySelector(
        '.lg\\:overflow-y-auto.lg\\:max-h-screen'
      );
      if (contentElement) {
        const isAtBottom =
          contentElement.scrollHeight - contentElement.scrollTop <=
          contentElement.clientHeight + 50;
        if (isAtBottom) {
          const lastSection = sectionElements[sectionElements.length - 1];
          if (lastSection) {
            mostVisibleSection = lastSection;
          }
        }
      }

      if (mostVisibleSection) {
        setActiveSection(mostVisibleSection.id);
        const activeToCItem = document.querySelector(
          `[data-toc-id="${mostVisibleSection.id}"]`
        );
        if (activeToCItem) {
          activeToCItem.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest'
          });
        }
      }
    };

    const contentElement = document.querySelector(
      '.lg\\:overflow-y-auto.lg\\:max-h-screen'
    );

    if (contentElement && isLargeScreen) {
      contentElement.addEventListener('scroll', handleScroll);
      handleScroll();
    }

    return () => {
      if (contentElement) {
        contentElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, [isLargeScreen, tableOfContents]);

  const scrollToSection = (id: string) => {
    setShowMobileNav(false);
    setTimeout(() => {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  useEffect(() => {
    const { fromPrivacyLink } = router.query;
    const isFromPrivacyPolicyLink = fromPrivacyLink === 'true';

    if (isFromPrivacyPolicyLink) {
      const privacyPolicySection = document.getElementById(
        'track-ip-address-section'
      );
      if (privacyPolicySection) {
        privacyPolicySection.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [router.query]);

  const handleBackClick = () => {
    if (ref) {
      router.push(ref as string); // Redirect back to the referring page
    } else {
      router.push('/'); // Default back to the home page if no ref found
    }
  };

  return (
    <>
      <Head>
        <meta name="og:title" content={'Privacy Policy - See Sight Tours'} />
        <meta
          name="og:description"
          content={'The Privacy Policy Of See Sight Tours '}
        />
        <title>Privacy Policy - See Sight Tours</title>
        <meta
          name="description"
          content={'The Privacy Policy Page Of See Sight Tours '}
        />
        <link
          rel="canonical"
          href={'https://www.seesight-tours.com/privacy-policy'}
        />
        <meta name="og:image" content="/vertical-seesight-logo.png" />
      </Head>

      <div className="min-h-screen flex flex-col lg:flex-row justify-center mt-[30px]  sm:px-8">
        <div className="max-w-[110rem] w-full flex flex-col lg:flex-row">
          <div className="sticky bg-white top-0">
            <button
              className="font-semibold items-center text-sm leading-[19.5px] flex gap-4 sm:hidden p-4 cursor-pointer hover:underline hover:text-primary"
              onClick={handleBackClick}
            >
              <Image
                src={'/black-left-arrow.svg'}
                alt="left arrow"
                width={8}
                height={10}
              />
              Back
            </button>
          </div>
          <div className=" lg:hidden px-4">
            <div className="">
              <h1 className="ml-2 text-[20px] font-bold mb-2 text-center">
                See Sight Tours Privacy Policy
              </h1>
              <div className="flex justify-between items-center px-5 bg-blue-50 h-[65px]">
                <h1 className="text-[16px] font-bold ">Table Of Contents</h1>
                <button
                  onClick={() => setShowMobileNav(!showMobileNav)}
                  className="flex items-center gap-2 w-[43px] h-[30px] text-[#0881D8] text-[12px] border-[0.5px] bg-[#0881d8] bg-opacity-5 border-[#0881D8] px-1 py-1 rounded-md mt-1"
                >
                  {showMobileNav ? <>Hide</> : <>Show</>}
                </button>
              </div>
            </div>
          </div>
          <div
            className={`lg:w-[403px] lg:p-6 lg:mt-8 lg:h-screen lg:sticky lg:top-0  ${
              !showMobileNav && 'hidden lg:block'
            }`}
          >
            <nav className="h-auto lg:h-[calc(100vh-8rem)] overflow-y-visible lg:overflow-y-auto max-h-[130vh] [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
              <div className="lg:hidden bg-blue-50 p-3  mx-4">
                {tableOfContents.map(item => (
                  <div
                    key={item.id}
                    className="min-h-[43px] sm:min-h-[50px] md:min-h-[55px] flex items-center px-4 py-2 cursor-pointer"
                    onClick={() => scrollToSection(item.id)}
                  >
                    <span className="text-[#0881d8] text-[14px] font-normal leading-normal  sm:ml-4">
                      {item.title}
                    </span>
                  </div>
                ))}
              </div>
              <div className="hidden lg:block">
                {tableOfContents.map(item => (
                  <div
                    key={item.id}
                    data-toc-id={item.id}
                    className={`min-h-[43px] sm:min-h-[50px] md:min-h-[55px] flex items-center px-4 py-2 cursor-pointer transition-colors relative ${
                      activeSection === item.id
                        ? 'bg-gray-100'
                        : 'hover:bg-gray-50'
                    } border-b border-r border-gray-200`}
                    onClick={() => scrollToSection(item.id)}
                  >
                    {activeSection === item.id && (
                      <div className="absolute left-0 w-1.5 h-[50px] bg-[#1F2C5B] rounded-tr-[5px] rounded-br-[5px] scroll-smooth"></div>
                    )}
                    <span className="text-[#444444] text-base font-normal leading-normal no-underline sm:ml-4">
                      {item.title}
                    </span>
                  </div>
                ))}
              </div>
            </nav>
          </div>

          <div className="flex-1 p-6 max-w-[886px] overflow-y-visible lg:overflow-y-auto lg:max-h-screen scrollbar-hide">
            <div className="hidden lg:block mb-8 mt-7">
              <h1 className="text-2xl font-bold mb-[30px]">Privacy Policy</h1>
            </div>

            <p className="text-justify text-[#666666] text-base font-normal leading-relaxed">
              See Sight Tours is committed to protecting your privacy. This
              Privacy Policy describes how we collect, use, disclose, and
              protect your information when you visit our website or interact
              with us in any manner. This policy complies with applicable laws
              including GDPR, CCPA/CPRA, TCPA, CASL, and COPPA.
            </p>

            <section id="scope" className="mb-2 mt-2">
              <h2 className="text-[#444444] text-lg font-bold leading-relaxed mb-4">
                The Scope of This Policy
              </h2>
              <p className="text-justify text-[#666666] text-base font-normal leading-relaxed">
                This policy applies to all See Sight Tours users, including
                Travelers and Guides (including Guide applicants), and all See
                Sight Tours platforms and services, including our applications,
                websites, technology, facilities, and other services
                (collectively, the “See Sight Tours Platform”). This policy
                applies only to personal information, not to aggregate
                information or information that does not identify you.
              </p>
            </section>

            <section id="info-collect" className="mb-3">
              <h2 className="text-[#444444] text-lg font-semibold leading-relaxed mb-2">
                The Information We Collect
              </h2>
              <h3 className="font-bold text-[#444444]">
                Personal Information:
              </h3>
              <p className="text-[#666666] text-base font-normal leading-relaxed mb-3">
                When you contact us via our website, phone, or email, we may
                collect personal information you provide—such as your full name,
                phone number, email address, and mailing address
              </p>
              <h3 className="font-bold text-[#444444]">
                Automatically Collected Information:
              </h3>
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                When you visit our website, we may automatically collect details
                about your device and usage (e.g., IP address, browser type,
                operating system, referring URLs, and pages viewed).
              </p>
            </section>

            <section id="info-provide" className="mb-4">
              <h2 className="text-[#444444] text-lg font-extrabold leading-relaxed mb-2">
                A. Information You Provide to Us
              </h2>
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                <strong>Account Registration:</strong>
                When you create an account with See Sight Tours, we collect the
                information you provide us, such as your name, email address,
                phone number, birth date, profile photo, and payment
                information. We may ask that you provide additional information
                related to the identity of your account, such as documents
                related to identification (e.g., driver’s license), a profile
                picture, or “selfie” imagery.{' '}
              </p>
              <br />
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                <strong>Guide Information: </strong>
                If you apply to be a Guide, we will collect the information you
                provide in your application, including your name, email address,
                phone number, birth date, profile photo, physical address,
                government identification number (such as social security
                number), Guide’s license information, vehicle information, and
                car insurance information. We collect the payment information
                you provide us, including your bank routing numbers and tax
                information. Depending on where you want to drive, we may also
                ask for additional business license or permit information or
                other information to manage driving and programs relevant to
                that location. We may need additional information from you at
                some point after you become a Guide, including information to
                confirm your identity (like “selfie” imagery).
              </p>
              <br />
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                <strong>Ratings and Feedback: </strong>
                When you rate and provide feedback about the See Sight Tours
                Platform and services (including about Travelers or Guides), we
                collect all of the information you provide in your feedback.
              </p>
              <br />
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                <strong>Communications: </strong>
                When you contact us or we contact you, including through surveys
                or research projects, we collect any information that you
                provide, including the contents of the messages or attachments
                you send us
              </p>
            </section>
            <section id="info-platform">
              <h2 className="text-[#444444] text-lg font-extrabold leading-relaxed mb-4">
                B. Information We Collect When You Use the See Sight Tours
                Platform
              </h2>
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                <strong>Location Information: </strong>
                Great tours start with an easy and accurate pickup. The See
                Sight Tours Platform collects location information (including
                GPS and WiFi data, IP address, and Bluetooth data) differently
                depending on your See Sight Tours app settings and device
                permissions as well as whether you are using the platform as a
                See Sight Tours guide partner or a Traveler.
              </p>
              <br />
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                <li>
                  <strong>Travelers: </strong> We collect your device’s precise
                  location when you open and use the See Sight Tours app,
                  including while the app is running in the background from the
                  time you begin your tour until it ends. See Sight Tours also
                  tracks the precise location of vehicles at all times.
                </li>
                <li>
                  <strong>Guides: </strong> We collect your device’s precise
                  location when you open and use the app, including while the
                  app is running in the background. We also collect precise
                  locations for a limited time after you exit Guide mode to
                  detect ride incidents and continue collecting them until a
                  reported or detected incident is no longer active.
                </li>
              </p>
              <br />
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                <strong>Usage Information: </strong>
                We collect information about your use of the See Sight Tours
                Platform, including ride information like the date, time,
                destination, distance, route, payment, and whether you used a
                promotional or referral code. Our application may collect mobile
                sensor data, such as speed, direction, height, acceleration,
                deceleration, and other technical data. We also collect
                information about your interactions with the See Sight Tours
                Platform like our apps and websites, including the pages and
                content you view and the dates and times of your use.
              </p>
              <br />
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                <strong> Device Information: </strong>
                We collect information about the devices you use to access the
                See Sight Tours Platform, including device model, IP address,
                type of browser, version of operating system, identity of
                carrier and manufacturer, radio type (such as 4G), preferences
                and settings (such as preferred language), application
                installations, device identifiers, advertising identifiers, and
                push notification tokens. If you are a Guide, we also collect
                mobile sensor data from your device (such as speed, direction,
                height, acceleration, deceleration, and other technical data).
                If you have installed a See Sight Tours Platform device in your
                vehicle (e.g., a See Sight Tours dashboard device or tablet),
                that device may similarly collect sensor data and other
                information like location, as described when you choose and set
                up such devices.
              </p>
              <br />
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                <strong>Communications Between Travelers and Guides:</strong>
                We work with a third party to facilitate phone calls and text
                messages between Travelers and Guides without sharing either
                party’s actual phone number with the other. But while we use a
                third party to provide the communication service, we collect
                information about these communications and See Sight Tours
                Platform chat communications, including the participants’ phone
                numbers, the date and time, and the contents of SMS and chat
                messages. For security purposes, we may also monitor or record
                the contents of phone calls made through the See Sight Tours
                Platform, but we will always let you know we are about to do so
                before the call begins.{' '}
              </p>
              <br />
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                <strong>Address Book Contacts: </strong>
                You may set your device permissions to grant See Sight Tours
                access to your contact lists and direct See Sight Tours to
                access your contact list, for example, to help you refer friends
                to See Sight Tours. If you choose to do this, we will access the
                names and contact information of the people in your address
                book.
              </p>
              <br />
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                <strong>Calendar Information: </strong>
                You may set your device permissions or otherwise grant See Sight
                Tours access to your chosen calendar and direct See Sight Tours
                to access calendar information, for example, to help you get
                alerts for your booked tour. If you choose to do this, we will
                access and store information available in your calendar to use
                in providing you with these optional features. See Sight Tours
                will adhere to the applicable calendar provider policies,
                including Google’s API Services User Data Policy, which impose
                certain limitations on the use of data collected from user’s
                calendar accounts. You may remove See Sight Tours’s access to
                your accounts at any time either in-app or through your account
                provider settings.{' '}
              </p>
              <br />
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                <strong>
                  Cookies, Analytics, and Third-Party Technologies:{' '}
                </strong>
                We collect information through the use of “cookies”, tracking
                pixels, data analytics tools like Google Analytics, SDKs, and
                other third-party technologies to understand how you navigate
                through the See Sight Tours Platform and interact with
                advertisements, to make your See Sight Tours experience safer,
                to learn what content is popular, to improve your site
                experience, to serve you better ads on other sites, and to save
                your preferences. Cookies are small text files that web servers
                place on your device; they are designed to store basic
                information and to help websites and apps recognize your
                browser. We may use both session cookies and persistent cookies.
                A session cookie disappears after you close your browser. A
                persistent cookie remains after you close your browser and may
                be accessed every time you use the See Sight Tours Platform.
              </p>
              <br />
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                <strong>Inferences: </strong>
                We may infer certain information from your interactions with the
                See Sight Tours Platform and other personal information
                available to us. For example, if you frequently book tours, we
                may infer you are a frequent traveler. We may also draw
                inferences from the information you provide to us, such as
                inferring your gender based on your first name.
              </p>
            </section>
            <section id="info-sources" className="mt-3">
              <h2 className="text-[#444444] text-lg font-extrabold leading-relaxed mb-4">
                C. Information We Collect From Other Sources
              </h2>
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                Service Providers and Other Parties: Service providers and other
                parties provide us with information needed for core aspects of
                the See Sight Tours Platform, as well as for additional
                services, programs, loyalty benefits, and promotions that can
                enhance your See Sight Tours experience. These service providers
                and other parties include background check providers, insurance
                partners, financial service providers, marketing providers, and
                other businesses. We obtain the following information about you
                from these parties:
              </p>
              <br />
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                <li>
                  Service providers and other parties provide us with
                  information needed for core aspects of the See Sight Tours
                  Platform, as well as for additional services, programs,
                  loyalty benefits, and promotions that can enhance your See
                  Sight Tours experience. These service providers and other
                  parties include background check providers, insurance
                  partners, financial service providers, marketing providers,
                  and other businesses. We obtain the following information
                  about you from these parties:
                </li>
                <li>
                  Information about your participation in third-party programs
                  that provide things like insurance coverage and financial
                  instruments, such as insurance, payment, transaction, and
                  fraud detection information;
                </li>
                <li>
                  Information to operationalize loyalty and
                  promotional/marketing programs or applications, services, or
                  features you choose to connect or link to your See Sight Tours
                  account, such as information about your use of such programs,
                  applications, services, or features; and
                </li>
                <li>
                  Information about you provided by specific services, such as
                  vehicle, demographic, and other information that helps us
                  provide services or to personalize our marketing.
                </li>
              </p>
              <br />
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                <strong>Referral Programs: </strong>
                Friends help friends use the See Sight Tours Platform. If
                someone refers you to See Sight Tours, we will collect
                information about you from that referral including your name and
                contact information.
              </p>
              <br />
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                <strong>Other Users and Sources: </strong>
                <span>
                  Other users or public or third-party sources such as law
                  enforcement, insurers, media, or pedestrians may provide us
                  information about you, for example as part of an investigation
                  into an incident or to provide you support.
                </span>
              </p>
            </section>
            <section id="info-use" className="mt-3">
              <h2 className="text-[#444444] text-lg font-extrabold leading-relaxed mb-4">
                How We Use Your Information
              </h2>
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                We use your information to:
                <ul>
                  <li>
                    <strong>Communicate with You: </strong>
                    Respond to inquiries, send administrative communications,
                    and keep you informed about your case or our services.
                  </li>
                  <li>
                    <strong>Marketing and Promotional Communications:</strong>:
                    With your explicit consent, send newsletters, updates, or
                    marketing offers via email, phone, or text message.
                  </li>
                  <li>
                    <strong> Legal Compliance:</strong>: Comply with laws,
                    respond to legal requests, or protect our rights.
                  </li>
                </ul>
              </p>
              <br />
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                <strong>Providing the See Sight Tours Platform: </strong>
                We use your personal information to provide an intuitive,
                useful, efficient, and worthwhile experience on our platforms.
                To do this, we use your personal information to:
              </p>
              <br />
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                <li>
                  Verify your identity and maintain your account, settings, and
                  preferences;
                </li>
                <li>Connect you to your tours and track their progress;</li>
                <li>
                  Provide various See Sight Tours Platform offerings to you,
                  such as vehicle services and third-party promotional
                  advertisements that may be personalized to you;
                </li>
                <li>Calculate prices and process payments;</li>
                <li>
                  Allow Travelers and Guides to connect regarding their ride and
                  to choose to share their location with others;
                </li>
                <li>
                  Communicate with you about your use of the See Sight Tours
                  Platform and experience;
                </li>
                <li>Collect feedback regarding your experience;</li>
                <li>
                  Facilitate additional services and programs with third
                  parties; and
                </li>
                <li>Operate contests, sweepstakes, and other promotions.</li>
              </p>
              <br />
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                <strong>
                  Maintaining the Security and Safety of the See Sight Tours
                  Platform and its Users:{' '}
                </strong>
                Providing you with a secure and safe experience drives our
                platform, both on the road and on our apps. To do this, we use
                your personal information to:
              </p>
              <br />
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                <li>Authenticate and verify users;</li>
                <li>
                  Verify that Guides and their vehicles meet safety
                  requirements;
                </li>
                <li>
                  Investigate and resolve incidents, accidents, and insurance
                  claims;
                </li>
                <li>
                  Encourage safe driving behavior and avoid unsafe activities;
                </li>
                <li>Find and prevent fraud; and</li>
                <li>
                  Block and remove unsafe or fraudulent users from the See Sight
                  Tours Platform.
                </li>
              </p>
              <br />
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                <strong>
                  Building and Maintaining the See Sight Tours Community:
                </strong>{' '}
                See Sight Tours works to be a positive part of the community. We
                use your personal information to:
              </p>
              <br />
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                <li>
                  Communicate with you about events, promotions, elections, and
                  campaigns;
                </li>
                <li>
                  Personalize and provide content, experiences, communications,
                  and targeted advertising to promote and grow the See Sight
                  Tours Platform; and
                </li>
                <li>
                  Help facilitate donations you choose to make through the See
                  Sight Tours Platform.
                </li>
              </p>
              <br />
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                <strong>Providing Customer Support: </strong>
                We work hard to provide the best experience possible, including
                supporting you when you need it. To do this, we use your
                personal information to:
              </p>
              <br />
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                <li>
                  Investigate and assist you in resolving questions or issues
                  you have regarding See Sight Tours Platform; and
                </li>
                <li>Provide you support or respond to you.</li>
              </p>
              <br />
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                <strong>Improving the See Sight Tours Platform: </strong>
                We are always working to improve your experience and provide you
                with new and helpful features. To do this, we use your personal
                information to:
              </p>
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                <li>Perform research, testing, and analysis;</li>
                <li>
                  Develop new products, features, partnerships, and services;
                </li>
                <li>
                  Prevent, find, and resolve software or hardware bugs and
                  issues; and
                </li>
                <li>
                  Monitor and improve our operations and processes, including
                  security practices, algorithms, and other modeling.
                </li>
              </p>
              <br />
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                <strong>
                  Responding to Legal Proceedings and Requirements:{' '}
                </strong>
                Sometimes the law, government entities, or other regulatory
                bodies impose demands and obligations on us with respect to the
                services we seek to provide. In such a circumstance, we may use
                your personal information to respond to those demands or
                obligations.
              </p>
            </section>
            <section id="info-disclose" className="mt-3">
              <h2 className="text-[#444444] text-lg font-extrabold leading-relaxed mb-4">
                How We Disclose Your Information
              </h2>
              <ul className="text-[#666666] text-base font-normal leading-relaxed list-disc pl-4">
                <li>
                  <strong>No Sale or Rental: </strong>
                  We do not sell or rent your personal information, including
                  your SMS consent or phone number, to any third party for
                  marketing purposes.
                </li>
                <li>
                  <strong>Service Providers: </strong> We may share your
                  information with trusted service providers who assist us
                  (e.g., for marketing, customer service, or technical support).
                  These providers are contractually obligated to protect your
                  data and use it solely for the purposes we specify.
                </li>
                <li>
                  <strong>Legal Requirements: </strong>
                  We may disclose your information when required by law,
                  regulation, or legal process, or if necessary to protect our
                  rights, property, or the safety of our users or others.
                </li>
              </ul>
            </section>
            <section id="see-sight-tours-users" className="mt-3">
              <h2 className="text-[#444444] text-lg font-extrabold leading-relaxed mb-4">
                A. Sharing Between See Sight Tours Users
              </h2>
              <h3 className="text-[#444444] text-lg font-semibold leading-relaxed">
                Travelers and Guides
              </h3>
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                <strong>
                  <i>Traveler information shared with Guide:</i>
                </strong>{' '}
                As part of facilitating the tour booking process and ensuring a
                seamless experience, we share with the Guide the Traveler&apos;s
                pickup and destination, location, name, profile photo, rating.
                Once the tour is finished, we also eventually share the
                Traveler&apos;s rating and feedback with the Guide. (We remove
                the Traveler&apos;s identity associated with ratings and
                feedback when we share it with Guides, but a Guide may be able
                to identify the Traveler that provided the rating or feedback.)
              </p>
              <br />
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                <strong>
                  <i>Guide information shared with Traveler:</i>
                </strong>{' '}
                Once a Traveler books a tour, we may share with the Traveler the
                Guide’s name, profile photo, preferred pronouns, rating,
                real-time location, and the vehicle make, model, color, and
                license plate, as well as other information in the Guide’s See
                Sight Tours profile, such as information Guides choose to add
                (like background information and reasons for tour guiding) and
                Guide statistics (like approximate number of tours and years as
                a Guide).
              </p>
              <br />
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                Although we help Travelers and Guides communicate with one
                another to arrange a pickup, we do not share your actual phone
                number or other contact information with other users when
                enabling this. If you report a lost or found item to us, we will
                seek to connect you with the relevant Traveler or Guide, which
                may include sharing actual contact information with your
                consent.
              </p>
              <br />
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                <span className="font-bold">Tours Paid For by Others: </span>
                If someone else pays for a tour on your behalf, we may share
                some or all of your tour details with that party. This may
                include your identity (such as name and contact information),
                the date, time, cost, rating given, region of the tour, and
                pick-up and drop-off locations.
              </p>
            </section>
            <section id="service-providers" className="mt-3">
              <h2 className="text-[#444444] text-lg font-extrabold leading-relaxed mb-4">
                B. Disclosures to Service Providers to Provide the See Sight
                Tours Mobile Application, and Other Parties
              </h2>
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                Depending on whether you’re a Traveler or a Guide, See Sight
                Tours may disclose the following categories of your personal
                information to provide you with a variety of the See Sight Tours
                Platform’s features and services:
              </p>
              <br />
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                <li>
                  Personal identifiers, such as your name, address, email
                  address, phone number, date of birth, government
                  identification number (such as social security number), and
                  driver&apos;s license information, vehicle information, and
                  car insurance information;
                </li>
                <li>
                  Financial information, such as bank routing numbers, tax
                  information, and any other payment information you provide us;{' '}
                </li>
                <li>
                  Commercial information, such as tour information,
                  Traveler/Guide statistics and feedback;
                </li>
                <li>
                  Internet or other electronic network activity information,
                  such as your IP address, type of browser, version of operating
                  system, carrier and/or manufacturer, device identifiers, and
                  mobile and other forms of advertising identifiers;
                </li>
                <li>Location data</li>
              </p>
              <br />
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                We disclose those categories of personal information to these
                other parties and service providers to fulfill the following
                purposes:
              </p>
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                <li>Maintaining and servicing your See Sight Tours account;</li>
                <li>
                  Processing or fulfilling tours or other offerings of the See
                  Sight Tours Platform;
                </li>
                <li>Providing you with customer service;</li>
                <li>Processing transactions and payments;</li>
                <li>Processing Guide applications;</li>
                <li>Verifying the identity of users;</li>
                <li>Detecting and preventing fraud and unsafe activity;</li>
                <li>Providing Guide loyalty and promotional programs;</li>
                <li>
                  Providing marketing and advertising services to See Sight
                  Tours, including to provide targeted and cross-contextual
                  behavioral advertising personalized to you;
                </li>
                <li>
                  Providing analytics services to and by See Sight Tours; and
                </li>
                <li>
                  Researching to develop and improve the See Sight Tours
                  Platform.
                </li>
              </p>
            </section>
            <section id="legal-reasons" className="mt-3">
              <h2 className="text-[#444444] text-lg font-extrabold leading-relaxed mb-4">
                C. For Legal Reasons and to Protect the See Sight Tours Platform
              </h2>
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                We may disclose your personal information in response to a legal
                obligation, or if we have determined that disclosing your
                personal information is reasonably necessary or appropriate to:
              </p>
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                <li>
                  Comply with any applicable federal, state, or local law or
                  regulation, civil, criminal, or regulatory inquiry,
                  investigation or legal process, enforceable governmental
                  request, or requirement as a condition to operate (e.g.,
                  operating permit, license or agreement);
                </li>
                <li>
                  Respond to legal process (such as a search warrant, subpoena,
                  summons, or court order);
                </li>
                <li>
                  Cooperate with law enforcement agencies concerning conduct or
                  activity that we reasonably and in good faith believe may
                  violate federal, state, or local law; or
                </li>
                <li>
                  Exercise or defend legal claims, protect against harm to our
                  rights, property, interests, or safety or the rights,
                  property, interests, or safety of you, third parties, or the
                  public as required or permitted by law.
                </li>
              </p>
            </section>
            <section id="sale-or-merger" className="mt-3">
              <h2 className="text-[#444444] text-lg font-extrabold leading-relaxed mb-4">
                D. In Connection with the Sale or Merger{' '}
              </h2>
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                We may disclose your personal information while negotiating or
                in relation to a change of corporate control such as a
                restructuring, merger, or sale of our as
              </p>
            </section>
            <section id="further-direction" className="mt-3">
              <h2 className="text-[#444444] text-lg font-extrabold leading-relaxed mb-4">
                E. Upon Your Further Direction
              </h2>
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                With your permission or upon your direction, we may disclose
                your personal information to interact with a third party or for
                other purposes.
              </p>
            </section>
            <section id="info-protect" className="mt-3">
              <h2 className="text-[#444444] text-lg font-extrabold leading-relaxed mb-4">
                How We Store and Protect Your Information
              </h2>
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                We retain your information for as long as necessary to provide
                you and our other users with the See Sight Tours Platform. This
                means we keep your profile information for as long as you
                maintain an account. We retain transactional information such as
                tours and payments for an indefinite period to ensure we can
                perform legitimate business functions, such as accounting for
                tax obligations. We also retain your information as necessary to
                comply with our legal obligations, resolve disputes, and enforce
                our terms and policies. If you request account deletion, we will
                delete your information as outlined in the “Deleting Your
                Account” section below.
              </p>
              <br />
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                We take reasonable and appropriate measures designed to protect
                your personal information. However no security measures can be
                100% effective, and we cannot guarantee the security of your
                information, including against unauthorized intrusions or acts
                by third parties.{' '}
              </p>
            </section>
            <section id="data-retention" className="mt-3">
              <h2 className="text-[#444444] text-lg font-extrabold leading-relaxed mb-4">
                Data Retention
              </h2>
              <p className="text-[#666666] text-base font-normal leading-relaxed mt-4">
                We retain your personal data only as long as necessary for the
                purposes described or as required by law. For example, personal
                information may be retained for a period of five years after
                your last interaction with us. Please contact us for more
                details about our retention schedule.
              </p>
              <h2 className="text-[#444444] text-lg font-extrabold leading-relaxed mt-4">
                1. Data Security and Breach Notification
              </h2>
              <p className="text-[#666666] text-base font-normal leading-relaxed mt-4">
                We implement reasonable security measures to protect your
                personal information. In the event of a data breach that
                compromises your data, we will notify affected individuals and
                take remedial steps in accordance with applicable law.
              </p>
              <h2 className="text-[#444444] text-lg font-extrabold leading-relaxed mt-4">
                2. International Data Transfers
              </h2>
              <p className="text-[#666666] text-base font-normal leading-relaxed mt-4">
                If you access our services from outside our primary
                jurisdiction, please note that your information may be
                transferred to and processed in jurisdictions with different
                data protection laws. We take steps to ensure your data is
                adequately protected during such transfers.
              </p>
              <h2 className="text-[#444444] text-lg font-extrabold leading-relaxed mt-4">
                3. Contact Us
              </h2>
              <p className="text-[#666666] text-base font-normal leading-relaxed mt-4">
                For questions regarding these SMS Terms or your consent to
                receive SMS communications, please contact:
                <ul>
                  <li className="font-bold">See Sight Tours</li>
                  <li>Toll free: 1-888-961-6584</li>
                  <li>Email: info@seesight-tours.com </li>
                </ul>
              </p>
            </section>
            <section id="rights-and-choices" className="mt-3">
              <h2 className="text-[#444444] text-lg font-extrabold leading-relaxed mb-4">
                Your Rights And Choices Regarding Your Data{' '}
              </h2>
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                As explained more below, See Sight Tours provides ways for you
                to access and delete your personal information as well as
                exercise applicable data rights that give you certain control
                over your personal information.
              </p>
            </section>
            <section id="users" className="mt-3">
              <h2 className="text-[#444444] text-lg font-extrabold leading-relaxed mb-4">
                A. All Users
              </h2>
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                <strong> Email Subscriptions: </strong>You can always
                unsubscribe from our commercial or promotional emails by
                clicking unsubscribe in those messages. We will still send you
                transactional and relational emails about your use of the See
                Sight Tours Platform.
              </p>
              <br />
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                <strong> Push Notifications: </strong>You can opt out of
                receiving push notifications through your device settings.
                Please note that opting out of receiving push notifications may
                impact your use of the See Sight Tours mobile applications (such
                as receiving a notification that your tour guide has arrived for
                your pick-up).
              </p>
              <br />
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                <strong> Profile Information: </strong>You can review and edit
                certain account information you have chosen to add to your
                profile by logging in to your account settings and profile.
              </p>
              <br />
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                <strong> Location Information:</strong>You can prevent your
                device from sharing location information through your device’s
                system settings. But if you do, this may impact See Sight
                Tours’s ability to provide you with our full range of features
                and services. You may also control some elements of sharing your
                location with See Sight Tours in your See Sight Tours account
                settings.
              </p>
              <br />
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                <strong> Cookie Tracking:</strong>You can modify your cookie
                settings on your browser.
              </p>
              <br />
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                <strong> Accessing Your Information: </strong>You can see the
                information we have about you by logging into your account and
                viewing things like your profile, settings, preferences (like
                preferred pronouns and address shortcuts such as home and work),
                ride history, or payment information. In addition, you may have
                some information included in things we have sent to you, such as
                tour receipts.
              </p>
              <br />
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                <strong> Deleting Your Account: </strong>If you would like to
                delete your See Sight Tours account, you have the option to
                delete your account at any time in the See Sight Tours
                Platforms. When we delete your account, we may retain certain
                information for legitimate business purposes or to comply with
                legal or regulatory obligations. For example, we may retain your
                information to resolve open insurance claims, or we may be
                obligated to retain your information as part of an open legal
                claim. When we retain such data, we do so in ways designed to
                prevent its use for other purposes.
              </p>
            </section>
            <section id="location-specific" className="mt-3">
              <h2 className="text-[#444444] text-lg font-extrabold leading-relaxed mb-4">
                B. Location-Specific Rights
              </h2>
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                Some states such as California, Colorado, Connecticut, Montana,
                Oregon, Texas, Utah, and Virginia provide specific rights to
                residents of the state regarding personal information.
                Similarly, specific rights may be provided in Canada.
              </p>
              <br />
              <p>
                <h2 className="font-bold text-[#444444] text-lg">
                  Links to Third-Party Websites
                </h2>
                <p className="text-[#666666] text-base font-normal leading-relaxed mt-3">
                  Our website may contain links to third-party websites. We are
                  not responsible for the privacy practices or content of these
                  external sites. We encourage you to review the privacy
                  policies of any third-party sites you visit.
                </p>
              </p>
              <br />
              <p>
                <h2 className="font-bold text-[#444444] text-lg">
                  Changes to This Privacy Policy:
                </h2>
                <p className="text-[#666666] text-base font-normal leading-relaxed mt-3">
                  We may update this Privacy Policy from time to time. All
                  changes will be posted on this page with an updated “Last
                  Updated” date. We encourage you to review this policy
                  periodically.
                </p>
              </p>
              <br />
              <p>
                <h2 className="font-bold text-[#444444] text-lg">
                  Children&apos;s Privacy
                </h2>
                <p className="text-[#666666] text-base font-normal leading-relaxed mt-3">
                  We may update this Privacy Policy from time to time. All
                  changes will be posted on this page with an updated “Last
                  Updated” date. We encourage you to review this policy
                  periodically.
                </p>
              </p>
              <h2 className="text-[#444444] text-lg font-extrabold leading-relaxed mt-3">
                Contact Us
              </h2>
              <p className="text-[#666666] text-base font-normal leading-relaxed mt-3">
                For any questions or concerns regarding this Privacy Policy or
                our privacy practices, please contact:
                <ul>
                  <li className="font-bold">See Sight Tours</li>
                  <li>CA: 5400 Robinson St, Niagara Falls, ON L2G 2A6</li>
                  <li>
                    US: 310 Pine Ave, Niagara Falls, NY 14301, United States
                  </li>
                  <li>Toll free: 1-888-961-6584</li>
                  <li>Local: 1-289-271-9767</li>
                  <li>Fax: 1-888-908-6056</li>
                  <li>Email: info@seesight-tours.com</li>
                </ul>
              </p>
            </section>
            <section id="terms-of-service" className="mt-3">
              <h2 className="text-[#444444] text-lg font-extrabold leading-relaxed mb-4">
                SMS Terms of Service
              </h2>
              <p className="text-[#666666] text-base font-normal leading-relaxed">
                Welcome to See Sight Tours. By accessing or using our
                services—including receiving SMS communications—you agree to
                these Terms. If you do not agree, please do not use our
                services.
              </p>
              <h3 className="text-[#444444] font-bold leading-relaxed mt-4 text-lg">
                1. Consent for SMS Communication
              </h3>
              <ul className="mt-4">
                <li className="text-[#666666] text-base font-normal leading-relaxed">
                  <span className=" font-bold">Express Written Consent: </span>
                  By providing your phone number and opting in, you expressly
                  consent to receive SMS messages from See Sight Tours. We
                  maintain a record of this consent.
                </li>
                <li className="text-[#666666] text-base font-normal leading-relaxed">
                  <span className="font-bold">Scope of Consent: </span>
                  This consent covers SMS messages related to appointment
                  scheduling, reminders, post-visit instructions, lab
                  notifications, billing updates, and other service-related
                  communications.
                </li>
              </ul>
              <h3 className="text-[#444444] font-bold text-lg leading-relaxed mt-4">
                2. Message Frequency and Data Rates
              </h3>
              <ul>
                <li className="text-[#666666] text-base font-normal leading-relaxed mt-4">
                  <span className="font-bold">Frequency</span>: Message
                  frequency may vary based on your interactions with us. For
                  example, you may receive
                  <span className="font-bold">five messages per month</span>.
                </li>
                <li className="text-[#666666] text-base font-normal leading-relaxed mt-2">
                  <span className="font-bold">Data Rates: </span>
                  Standard message and data rates charged by your mobile service
                  provider may apply.
                </li>
              </ul>
              <h3 className="text-[#444444] text-lg font-bold leading-relaxed mt-4">
                3. Opt-Out and Help
              </h3>
              <ul>
                <li className="text-[#666666] text-base font-normal leading-relaxed mt-4">
                  <span className="font-bold">Opt-Out: </span>
                  You can opt out at any time by replying “STOP” to any SMS
                  message you receive.
                </li>
                <li className="text-[#666666] text-base font-normal leading-relaxed mt-2">
                  <span className="font-bold">Help: </span>
                  For assistance, reply “HELP” to any SMS or contact us directly
                  at 1-888-961-6584
                </li>
                <li className="text-[#666666] text-base font-normal leading-relaxed mt-2">
                  <span className="font-bold">Effect of Opt-Out: </span>
                  Please note that opting out may limit our ability to
                  communicate with you regarding your services
                </li>
              </ul>
              <h3 className="text-[#444444] text-lg font-bold leading-relaxed mt-4">
                4. Sender Identification and Record-Keeping
              </h3>
              <ul>
                <li className="text-[#666666] text-base font-normal leading-relaxed mt-4">
                  <span className="font-bold"> Sender Identification: </span>
                  All SMS messages from us will clearly identify See Sight Tours
                  as the sender and include our contact details.
                </li>
                <li className="text-[#666666] text-base font-normal leading-relaxed mt-2">
                  <span className="font-bold">Record -Keeping: </span>
                  We retain records of your SMS consent for a minimum of three
                  years, as required by applicable law (e.g., CASL).
                </li>
              </ul>
              <h3 className="text-[#444444] text-lg font-bold leading-relaxed mt-4">
                5. Use of SMS Data
              </h3>
              <p className="text-[#666666] text-base font-normal leading-relaxed mt-4">
                Any information obtained during the SMS consent process is used
                solely for the purposes described above. We do not share your
                SMS consent or phone number with third parties for marketing
                purposes.
              </p>
              <h3 className="text-[#444444] text-lg font-bold leading-relaxed mt-4">
                6. Changes to These SMS Terms
              </h3>
              <p className="text-[#666666] text-base font-normal leading-relaxed mt-4">
                We reserve the right to update these SMS Terms from time to
                time. Any changes will be posted on our website and, where
                feasible, communicated directly to you.
              </p>
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicy;
